// Ruedita de configuración del composer de "Crear desde cero".
//
// Ocupa el lugar donde antes vivía el "+": ahí adentro van los ajustes de la
// generación, que cambian según el toggle Imagen/Video:
//   • Imagen → formato + cantidad (1 a 6)
//   • Video  → formato + calidad + resolución + duración
//
// En MOBILE el menú además repite el toggle Imagen/Video arriba de todo: el
// toggle principal vive en el header del chrome y es fácil no verlo, así que
// esta es la red de seguridad.
//
// El popover se PORTALEA al body y se posiciona en coordenadas de viewport
// (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean). Antes vivía
// `absolute` respecto del botón, y por eso la fila que lo contiene tenía prohibido el
// `overflow-*`: cualquier scroll lo recortaba. Ahora esa fila scrollea en horizontal
// cuando las pastillas no entran, así que el menú tiene que vivir afuera.

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePosicionSobreElBoton } from '../../components/posicionSobreElBoton';
import { Settings2, Check, Minus, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { AYUDA_BORRADOR, esBorrador, RESOLUCIONES_VIDEO } from '../../../../lib/resolucionVideo';
import { EtiquetaBorrador } from '../../../../components/EtiquetaBorrador';
import {
  VIDEO_DURATION_MIN,
  videoDuracionMax,
  MAX_CANTIDAD,
  type OutputKind,
  type VideoQuality,
} from './directMode';
import {
  videoGeneralRatePerSec,
  type VideoResolution,
} from '../../../../utils/credits';
import { OutputKindToggle } from './OutputKindToggle';
import { useIsMdUp } from './useIsMdUp';

export interface ConfiguracionMenuProps {
  outputKind: OutputKind;
  onChangeOutputKind: (k: OutputKind) => void;
  /**
   * ¿Se dibuja la fila con el toggle Imagen|Video? Default `true`.
   *
   * `false` en la variante v2 del asistente, que sacó ese control de la pantalla
   * (ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo). `outputKind` se sigue
   * recibiendo: decide si el menú muestra "Cantidad" o los ajustes de video.
   */
  mostrarOutputKind?: boolean;
  /**
   * `true` → el trigger es SIEMPRE redondo solo-ícono, también en desktop.
   *
   * Lo usa el composer colapsado de v2, donde los controles comparten una única fila
   * con el placeholder: el rótulo "Configuración" se come ~110px de esa fila y el
   * placeholder queda partido. Expandido vuelve el pill con texto.
   */
  compacto?: boolean;
  formatSlug: string;
  onChangeFormat: (id: string) => void;
  cantidad: number;
  onChangeCantidad: (n: number) => void;
  durationSeconds: number;
  onChangeDuration: (s: number) => void;
  videoQuality: VideoQuality;
  onChangeVideoQuality: (q: VideoQuality) => void;
  videoResolution: VideoResolution;
  onChangeVideoResolution: (r: VideoResolution) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * El modo es SÓLO video (Creador de video, ADR-2026-09-07-modo-creador-video): la
   * ruedita no repite el toggle Imagen/Video en mobile —no hay nada que togglear— y
   * muestra directamente los ajustes de video.
   */
  soloVideo?: boolean;
}

export const ConfiguracionMenu: React.FC<ConfiguracionMenuProps> = ({
  outputKind,
  onChangeOutputKind,
  mostrarOutputKind = true,
  compacto = false,
  formatSlug,
  onChangeFormat,
  cantidad,
  onChangeCantidad,
  durationSeconds,
  onChangeDuration,
  videoQuality,
  onChangeVideoQuality,
  videoResolution,
  onChangeVideoResolution,
  open,
  onOpenChange,
  soloVideo = false,
}) => {
  const isMdUp = useIsMdUp();
  const [subOpen, setSubOpen] = useState(false);
  // El popover va portaleado al body: su posición se mide del botón
  // (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean).
  const btnRef = useRef<HTMLButtonElement>(null);
  const pos = usePosicionSobreElBoton(btnRef, open, 280);
  useEffect(() => {
    if (!open) setSubOpen(false);
  }, [open]);

  const formatName =
    FORMAT_OPTIONS_V2.find((f) => f.id === formatSlug)?.name ?? 'Cuadrado (1:1)';

  // El tope del slider depende de la calidad (básica 15s / profesional 30s).
  const durationMax = videoDuracionMax(videoQuality);
  const creditModel = videoQuality === 'profesional' ? 'pro' : 'standard';

  // Posición del thumb/progreso del slider de duración, en %.
  const durationPct =
    ((durationSeconds - VIDEO_DURATION_MIN) /
      (durationMax - VIDEO_DURATION_MIN)) *
    100;

  // Lista de formatos — sub-vista en mobile, flyout al costado en desktop.
  // SIN scroll: son 7 opciones y se muestran todas. Un `max-h` + overflow acá
  // obligaba a scrollear para ver Banner/Billboard, que es justo lo que se
  // quiere evitar en un selector corto.
  const formatContent = (showBack: boolean) => (
    <div className="flex flex-col gap-1">
      {showBack && (
        <button
          type="button"
          onClick={() => setSubOpen(false)}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
      )}
      {FORMAT_OPTIONS_V2.map((opt) => {
        const active = opt.id === formatSlug;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              onChangeFormat(opt.id);
              // Mobile vuelve al root; desktop deja el flyout abierto para
              // que se vea el tilde moverse.
              if (!isMdUp) setSubOpen(false);
            }}
            className={`w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition-colors ${
              active ? 'bg-[#1644EB0F]' : 'hover:bg-neutral-50'
            }`}
          >
            <span className="text-[14px] text-[#02062BE6]">{opt.name}</span>
            {active && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="relative shrink-0">
      {/* Mobile: redonda solo-ícono (espacio del composer). Desktop: pill con
          ícono + "Configuración" — el rótulo hace descubrible el menú. */}
      <button
        ref={btnRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-label="Configuración de la generación"
        aria-expanded={open}
        className={`inline-flex items-center justify-center gap-1.5 h-9 rounded-full transition-colors ${
          compacto ? 'w-9' : 'w-9 md:w-auto md:px-3.5'
        } ${
          open
            ? 'bg-neutral-200 text-[#02062BE6]'
            : 'bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3]'
        }`}
      >
        <Settings2 className="w-4 h-4 shrink-0" strokeWidth={2.25} />
        {!compacto && (
          <span className="hidden md:inline text-[13.5px] font-medium whitespace-nowrap">
            Configuración
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
          <div
            className="fixed inset-0 z-[55]"
            onClick={() => onOpenChange(false)}
          />
          <div
            className="fixed w-[280px] max-w-[calc(100vw-16px)] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56]"
            style={pos ? { left: pos.left, bottom: pos.bottom } : { visibility: 'hidden' }}
          >
            {isMdUp || !subOpen ? (
              <div className="flex flex-col">
                {/* Toggle Imagen|Video — SOLO mobile. En desktop el toggle de
                    arriba del input está siempre a la vista. Dos motivos más para
                    no mostrarlo, y son independientes: que la pantalla que monta el
                    menú no tenga ese control (en v2 del asistente no existe, y
                    dejarlo acá era el mismo switch escondido en un menú), y que el
                    modo sea sólo-video (`soloVideo`). */}
                {mostrarOutputKind && !soloVideo && (
                  <>
                    <div className="md:hidden px-2 pt-1.5 pb-2.5 flex justify-center">
                      <OutputKindToggle
                        value={outputKind}
                        onChange={onChangeOutputKind}
                        className="w-full"
                      />
                    </div>
                    <div className="md:hidden border-t border-black/[0.05] mb-1" />
                  </>
                )}

                {/* Formato → flyout en desktop, sub-vista en mobile. */}
                <button
                  type="button"
                  onClick={() => setSubOpen((v) => !v)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                    subOpen && isMdUp
                      ? 'bg-neutral-100'
                      : 'hover:bg-neutral-50 active:bg-neutral-100'
                  }`}
                >
                  <span className="text-[14.5px] text-[#02062BE6]">Formato</span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-[13.5px] text-[#3C3C43]/55 truncate max-w-[130px]">
                      {formatName}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40" />
                  </span>
                </button>

                {outputKind === 'imagen' ? (
                  /* Cantidad — stepper +/-, mismo control que InterfazV2. */
                  <div className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl">
                    <span className="min-w-0">
                      <span className="block text-[14.5px] text-[#02062BE6]">
                        Cantidad
                      </span>
                      <span className="block text-[12px] text-[#3C3C43]/55 mt-0.5 leading-snug">
                        Cada imagen consume créditos aparte.
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        aria-label="Menos"
                        disabled={cantidad <= 1}
                        onClick={() => onChangeCantidad(Math.max(1, cantidad - 1))}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                      <span className="w-6 text-center text-[14px] font-semibold text-[#02062BE6] tabular-nums">
                        {cantidad}
                      </span>
                      <button
                        type="button"
                        aria-label="Más"
                        disabled={cantidad >= MAX_CANTIDAD}
                        onClick={() =>
                          onChangeCantidad(Math.min(MAX_CANTIDAD, cantidad + 1))
                        }
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                  {/* Calidad — básica (Seedance 2, hasta 15s) o profesional
                      (Seedance 2.5, hasta 30s, tarifa mayor). Al bajar de calidad el
                      padre clampa la duración al tope nuevo. */}
                  <div className="w-full px-3 py-2.5">
                    <span className="block text-[14.5px] text-[#02062BE6] mb-2">
                      Calidad
                    </span>
                    <div
                      className="grid grid-cols-2 gap-1.5"
                      role="radiogroup"
                      aria-label="Calidad del video"
                    >
                      {(
                        [
                          { id: 'profesional', label: 'Profesional', sub: 'hasta 30s' },
                          { id: 'basica', label: 'Básica', sub: 'hasta 15s' },
                        ] as Array<{ id: VideoQuality; label: string; sub: string }>
                      ).map((opt) => {
                        const active = opt.id === videoQuality;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => onChangeVideoQuality(opt.id)}
                            className={`rounded-xl px-2 py-2 text-center transition-colors ${
                              active
                                ? 'bg-[#1644EB0F] ring-1 ring-brand-primary'
                                : 'bg-neutral-50 hover:bg-neutral-100 ring-1 ring-transparent'
                            }`}
                          >
                            <span
                              className={`block text-[13.5px] font-medium ${
                                active ? 'text-brand-primary' : 'text-[#02062BE6]'
                              }`}
                            >
                              {opt.label}
                            </span>
                            <span className="block text-[11px] text-[#3C3C43]/55 mt-0.5">
                              {opt.sub}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Resolución — cambia la tarifa por segundo (se muestra
                      la de la calidad activa, misma fuente que el cobro). */}
                  <div className="w-full px-3 py-2.5">
                    <span className="block text-[14.5px] text-[#02062BE6] mb-2">
                      Resolución
                    </span>
                    <div
                      className="grid grid-cols-3 gap-1.5"
                      role="radiogroup"
                      aria-label="Resolución del video"
                    >
                      {RESOLUCIONES_VIDEO.map((res) => {
                        const active = res === videoResolution;
                        return (
                          <button
                            key={res}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => onChangeVideoResolution(res)}
                            title={esBorrador(videoQuality, res) ? AYUDA_BORRADOR : undefined}
                            className={`rounded-xl px-1.5 py-2 text-center transition-colors ${
                              active
                                ? 'bg-[#1644EB0F] ring-1 ring-brand-primary'
                                : 'bg-neutral-50 hover:bg-neutral-100 ring-1 ring-transparent'
                            }`}
                          >
                            <span
                              className={`block text-[13px] font-medium tabular-nums ${
                                active ? 'text-brand-primary' : 'text-[#02062BE6]'
                              }`}
                            >
                              {res}
                            </span>
                            <span className="block text-[10.5px] text-[#3C3C43]/55 mt-0.5 tabular-nums">
                              {videoGeneralRatePerSec(creditModel, res)} cr/s
                            </span>
                            {/* 480p Profesional es borrador: se puede pasar a 1080p después
                                (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). */}
                            {esBorrador(videoQuality, res) && (
                              <EtiquetaBorrador compacta className="mt-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Duración — slider. El riel se dibuja a mano y el
                     `input[type=range]` va invisible encima, que es el patrón
                     del slider de tiers del pricing (PlanCard): así se
                     conserva el teclado y el arrastre nativos sin pelear con
                     los estilos por navegador. */}
                  <div className="w-full px-3 py-2.5">
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                      <span className="text-[14.5px] text-[#02062BE6]">
                        Duración
                      </span>
                      <span className="text-[13.5px] font-semibold text-brand-primary tabular-nums">
                        {durationSeconds}s
                      </span>
                    </div>
                    <div className="relative h-5 flex items-center">
                      {/* Riel */}
                      <div className="absolute inset-x-0 h-1.5 rounded-full bg-neutral-200" />
                      {/* Progreso */}
                      <div
                        className="absolute left-0 h-1.5 rounded-full bg-brand-primary"
                        style={{ width: `${durationPct}%` }}
                      />
                      {/* Thumb */}
                      <div
                        className="absolute w-4 h-4 rounded-full bg-white border-2 border-brand-primary shadow-[0_1px_3px_rgba(0,0,0,0.2)] pointer-events-none"
                        style={{
                          left: `calc(${durationPct}% - 8px)`,
                        }}
                      />
                      <input
                        type="range"
                        min={VIDEO_DURATION_MIN}
                        max={durationMax}
                        step={1}
                        value={durationSeconds}
                        onChange={(e) => onChangeDuration(Number(e.target.value))}
                        aria-label="Duración del video en segundos"
                        aria-valuetext={`${durationSeconds} segundos`}
                        className="absolute inset-0 size-full cursor-pointer opacity-0"
                        style={{ touchAction: 'none' }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-[#3C3C43]/45 tabular-nums">
                        {VIDEO_DURATION_MIN}s
                      </span>
                      <span className="text-[11px] text-[#3C3C43]/45 tabular-nums">
                        {durationMax}s
                      </span>
                    </div>
                  </div>
                  </>
                )}
              </div>
            ) : (
              formatContent(true)
            )}
            {/* DESKTOP: flyout al costado. Se abre hacia la DERECHA porque
                esta ruedita está anclada al borde izquierdo del composer. */}
            {isMdUp && subOpen && (
              <div className="absolute left-full bottom-0 ml-2 w-[240px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 animate-mode-swap">
                {formatContent(false)}
              </div>
            )}
          </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default ConfiguracionMenu;
